import React from 'react';

// Registry of preload functions for centralized prefetching
const prefetchRegistry: Array<{ name: string; preload: () => Promise<any> }> = [];
let prefetchStarted = false;

/**
 * Retries a dynamic module import up to maxRetries times with exponential backoff
 * to handle temporary network glitches or server cold-start compilation delays.
 */
async function retryImport<T>(
  importFunc: () => Promise<T>,
  retriesLeft = 3,
  delayMs = 300
): Promise<T> {
  try {
    return await importFunc();
  } catch (error) {
    if (retriesLeft <= 1) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return retryImport(importFunc, retriesLeft - 1, delayMs * 2);
  }
}

/**
 * A custom HOC wrapper that extends React.lazy with a `.preload()` method,
 * registering the import function into a global prefetch registry and providing auto-retry.
 */
export function lazyWithPrefetch<T extends React.ComponentType<any>>(
  importFunc: () => Promise<{ default: T } | any>,
  componentName: string
): React.LazyExoticComponent<T> & { preload: () => Promise<any> } {
  let cache: Promise<any> | null = null;

  const preload = () => {
    if (!cache) {
      cache = retryImport(importFunc, 3, 300).catch((err) => {
        console.warn(`[lazyWithPrefetch] Warning: Failed loading chunk for ${componentName} after retries:`, err);
        // Reset cache so subsequent user actions can retry cleanly
        cache = null;
        throw err;
      });
    }
    return cache;
  };

  // Add to our registry of prefetchable items
  prefetchRegistry.push({ name: componentName, preload });

  // Use standard React.lazy but resolve with preload cache
  const LazyComp = React.lazy(() => {
    return preload()
      .then((module) => {
        if (module && module.default) {
          return module;
        }
        const component = module?.default || (module && typeof module === 'object' ? Object.values(module)[0] : module);
        if (!component) {
          throw new Error(`[lazyWithPrefetch] Component "${componentName}" default export was not found.`);
        }
        return { default: component };
      })
      .catch((err) => {
        console.error(`[lazyWithPrefetch] Failed rendering component "${componentName}":`, err);
        throw err;
      });
  });

  return Object.assign(LazyComp, { preload }) as any;
}

/**
 * Prefetches common assets (dynamic components, translation datasets, or sounds)
 * sequentially in the background after the dashboard loads.
 */
export function prefetchCommonAssets(
  loadLanguageData?: (lang: any) => Promise<any>,
  currentLang?: string
) {
  if (typeof window === 'undefined') return;
  if (prefetchStarted) return;
  prefetchStarted = true;

  // Delay prefetching so initial dashboard render is smooth and fast
  const delay = 1500;
  
  setTimeout(() => {
    console.log('[Prefetcher] Initializing background asset prefetching...');

    // 1. Prefetch lazy-loaded components sequentially with brief idle windows (DEACTIVATED to reduce unused JS bytes on initial load)
    const prefetchComponents = async () => {
      // Disabled to defer loading scripts strictly on-demand (e.g. on hover or navigate)
      console.log('[Prefetcher] Dynamic component auto-preload deactivated to save network bandwidth.');
    };

    // 2. Prefetch offline translation modules for other languages
    const prefetchLanguages = async () => {
      if (!loadLanguageData) return;
      const targetLanguages = ['en', 'ur', 'ar'].filter((l) => l !== currentLang);
      
      for (const lang of targetLanguages) {
        try {
          await loadLanguageData(lang as any);
          await new Promise((resolve) => setTimeout(resolve, 200));
        } catch (err) {
          console.warn(`[Prefetcher] Failed to prefetch language data for "${lang}":`, err);
        }
      }
      console.log('[Prefetcher] Alternative language datasets fully cached.');
    };

    // Run the prefetching tasks in sequence
    prefetchComponents().then(() => prefetchLanguages());
  }, delay);
}
